import { FormEditTabType, FormSettingsTabType } from '@doss/common-all'
import {
  DetailedForm,
  DetailedFormCategory,
  DetailedFormField,
  DetailedFormPage,
} from '../forms/types'
import {
  DEFAULT_SETTINGS_TAB,
  DEFAULT_TAB,
  FormFieldTypeToInputType,
  InputItem,
  InputItems,
  InputItemsMap,
} from './constants'
import {
  FormSettingsDragAndDropType,
  FormSettingsDropTargetType,
  FormSettingsSourceType,
} from './types'

/**
 * Set of valid form edit tab types
 */
const validFormEditTabTypes = new Set<string>([
  FormEditTabType.FORM_CONTENT,
  FormEditTabType.SETTINGS,
])

/**
 * Set of valid form settings tab types
 */
const validFormSettingsTabTypes = new Set<string>([
  FormSettingsTabType.DETAILS,
  FormSettingsTabType.WORKFLOWS,
  FormSettingsTabType.REDIRECT,
  FormSettingsTabType.SHARING,
])

/**
 * Typeguard to check that tab string is valid form edit tab type
 */
const isFormEditTabType = (value: string): value is FormEditTabType => {
  return validFormEditTabTypes.has(value)
}

/**
 * Converts the query parameter to the FormEditTabType enum value
 */
export const getTabFromQuery = (tab: string | string[] | undefined): FormEditTabType => {
  if (Array.isArray(tab)) {
    tab = tab[0]
  }

  if (tab && isFormEditTabType(tab)) {
    return tab
  }

  return DEFAULT_TAB
}

/**
 * Typeguard to check that tab string is valid form settings tab type
 */
const isFormSettingsTabType = (value: string): value is FormSettingsTabType => {
  return validFormSettingsTabTypes.has(value)
}

/**
 * Converts the query parameter to the FormSettingsTabType enum value
 */
export const getSettingsTabFromQuery = (
  tab: string | string[] | undefined,
): FormSettingsTabType => {
  if (Array.isArray(tab)) {
    tab = tab[0]
  }

  if (tab && isFormSettingsTabType(tab)) {
    return tab
  }
  return DEFAULT_SETTINGS_TAB
}

/**
 * This function takes your field and gives you the input item to be used
 * in the type dropdown for this field
 * @param field The field you want to render
 * @returns The input item used in the type dropdown for this field
 */
export function getInputItemFromField(field: DetailedFormField): InputItem {
  const inputItemType = FormFieldTypeToInputType[field.type]
  return InputItemsMap.get(inputItemType) || InputItems[0]
}

/**
 * Form data that you get from the backend is nested. The form contains a
 * list of pages. Each of those pages contains a list of categories.
 * And finally, each of those categories contains a list of fields.
 * This function takes the form data and flattens it into a list of fields,
 * categories, and pages.
 * @param form The detailed form you get from the get form endpoint
 * @returns all of the fields, categories, and pages in the form
 */
export function extractComponentsFromForm(form: DetailedForm): {
  fields: DetailedFormField[]
  categories: DetailedFormCategory[]
  pages: DetailedFormPage[]
} {
  const fields = form.pages
    .flatMap((page) => page.categories)
    .flatMap((category) => category.fields)
  const categories = form.pages.map((page) => page.categories).flat()
  const pages = form.pages
  return { fields, categories, pages }
}

export function getDropType(
  sourceData: { draggableType?: FormSettingsSourceType },
  targetData: { dropTargetType?: FormSettingsDropTargetType },
): FormSettingsDragAndDropType {
  if (targetData.dropTargetType === FormSettingsDropTargetType.PAGE_IN_OUTLINE) {
    if (sourceData.draggableType === FormSettingsSourceType.PAGE) {
      return FormSettingsDragAndDropType.MOVE_PAGE_ON_PAGE
    }
    if (sourceData.draggableType === FormSettingsSourceType.CATEGORY) {
      return FormSettingsDragAndDropType.MOVE_CATEGORY_ON_PAGE
    }
    if (sourceData.draggableType === FormSettingsSourceType.EMPTY_PAGE) {
      return FormSettingsDragAndDropType.CREATE_PAGE_ON_PAGE
    }
  }

  if (targetData.dropTargetType === FormSettingsDropTargetType.FORM_OUTLINE) {
    if (sourceData.draggableType === FormSettingsSourceType.PAGE) {
      return FormSettingsDragAndDropType.MOVE_PAGE_ON_OUTLINE
    }
    if (sourceData.draggableType === FormSettingsSourceType.EMPTY_PAGE) {
      return FormSettingsDragAndDropType.CREATE_PAGE_ON_OUTLINE
    }
  }

  if (targetData.dropTargetType === FormSettingsDropTargetType.CATEGORY_IN_PAGE) {
    if (sourceData.draggableType === FormSettingsSourceType.CATEGORY) {
      return FormSettingsDragAndDropType.MOVE_CATEGORY_ON_CATEGORY
    }
    if (sourceData.draggableType === FormSettingsSourceType.EMPTY_CATEGORY) {
      return FormSettingsDragAndDropType.CREATE_CATEGORY_ON_CATEGORY
    }
    if (sourceData.draggableType === FormSettingsSourceType.FIELD) {
      return FormSettingsDragAndDropType.MOVE_FIELD_ON_CATEGORY
    }
    if (sourceData.draggableType === FormSettingsSourceType.EMPTY_FIELD) {
      return FormSettingsDragAndDropType.CREATE_FIELD_ON_CATEGORY
    }
  }

  if (targetData.dropTargetType === FormSettingsDropTargetType.PAGE_IN_EDITOR) {
    if (sourceData.draggableType === FormSettingsSourceType.CATEGORY) {
      return FormSettingsDragAndDropType.MOVE_CATEGORY_ON_PAGE
    }
    if (sourceData.draggableType === FormSettingsSourceType.EMPTY_CATEGORY) {
      return FormSettingsDragAndDropType.CREATE_CATEGORY_ON_PAGE
    }
  }

  if (targetData.dropTargetType === FormSettingsDropTargetType.FIELD_IN_CATEGORY) {
    if (sourceData.draggableType === FormSettingsSourceType.FIELD) {
      return FormSettingsDragAndDropType.MOVE_FIELD_ON_FIELD
    }
    if (sourceData.draggableType === FormSettingsSourceType.EMPTY_FIELD) {
      return FormSettingsDragAndDropType.CREATE_FIELD_ON_FIELD
    }
  }

  throw new Error('Invalid drop target type')
}
